import { barX, barY, defineChart } from "@tanstack/charts";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { scaleOrdinal } from "@tanstack/charts/scales/ordinal";
import { BrandChartTheme } from "../BrandChart";
import { BarChartItem } from "./BarChart.types";

/** Average glyph width of 12px Outfit tick labels. */
const AVERAGE_CHAR_WIDTH = 6.5;
const MIN_LABEL_CHARS = 8;
const ROTATED_LABEL_CHARS = 24;

export type BarChartDefinitionOptions = {
  orientation: "horizontal" | "vertical";
  brandTheme: BrandChartTheme;
  valueAxisLabel?: string;
  labelAxisLabel?: string;
  showXAxis?: boolean;
  valueNumTicks?: number;
  /** Bar labels, in the order matching `colorRange`. */
  keys?: string[];
  colorRange?: string[];
  maxXAxisHeight?: number;
  margins?: { t: number; r: number };
};

export const truncateLabel = (label: string, maxChars: number) => {
  if (label.length <= maxChars) {
    return label;
  }

  return `${label.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
};

const formatValue = (value: number) => value.toLocaleString();

const valueScale = (data: BarChartItem[]) =>
  data.every((item) => item.value === 0)
    ? // An all-zero domain collapses the axis; keep a visible 0–1 range.
      scaleLinear().domain([0, 1])
    : scaleLinear;

const barPaint = ({
  brandTheme,
  keys,
  colorRange,
}: BarChartDefinitionOptions) =>
  colorRange && keys
    ? {
        color: "label" as const,
        colorOptions: {
          scale: scaleOrdinal<string, string>().domain(keys).range(colorRange),
        },
      }
    : { fill: brandTheme.primary, colorOptions: undefined };

/**
 * Builds the bar chart spec for a known surface width. The width only drives
 * label truncation and rotation; TanStack Charts still owns scale ranges and
 * guide margins.
 */
export const buildBarChartDefinition = (
  data: BarChartItem[],
  options: BarChartDefinitionOptions,
  width: number,
) => {
  const {
    orientation,
    brandTheme,
    valueAxisLabel,
    labelAxisLabel,
    showXAxis = true,
    valueNumTicks = 6,
    maxXAxisHeight,
    margins,
  } = options;
  const { color, fill, colorOptions } = barPaint(options);

  const shared = {
    theme: brandTheme.theme,
    margin: margins ? { top: margins.t, right: margins.r } : undefined,
    ...(colorOptions ? { color: colorOptions } : {}),
  };

  const valueAxis = {
    scale: valueScale(data),
    nice: true,
    grid: true,
    axis: {
      line: false,
      label: valueAxisLabel,
      ticks: { count: valueNumTicks, size: 0, format: formatValue },
    },
  };

  if (orientation === "horizontal") {
    const maxChars = Math.max(
      MIN_LABEL_CHARS,
      Math.floor((width * 0.35) / AVERAGE_CHAR_WIDTH),
    );

    return defineChart({
      ...shared,
      marks: [
        barX(data, {
          x: "value",
          y: "label",
          key: "label",
          color,
          fill,
          radius: { end: 3 },
        }),
      ],
      scales: {
        x: valueAxis,
        y: {
          scale: () => scaleBand<string>().paddingInner(0.2).paddingOuter(0),
          axis: {
            label: labelAxisLabel,
            ticks: {
              size: 0,
              format: (label: string) => truncateLabel(label, maxChars),
            },
          },
        },
      },
    });
  }

  const bandWidth = (width / Math.max(1, data.length)) * 0.8;
  const fittingChars = Math.floor(bandWidth / AVERAGE_CHAR_WIDTH);
  const rotate = fittingChars < MIN_LABEL_CHARS;
  const maxChars = rotate
    ? maxXAxisHeight
      ? Math.floor(maxXAxisHeight / AVERAGE_CHAR_WIDTH)
      : ROTATED_LABEL_CHARS
    : fittingChars;

  return defineChart({
    ...shared,
    marks: [
      barY(data, {
        x: "label",
        y: "value",
        key: "label",
        color,
        fill,
        radius: { end: 3 },
      }),
    ],
    scales: {
      x: {
        scale: () => scaleBand<string>().paddingInner(0.2).paddingOuter(0.2),
        axis: showXAxis
          ? {
              label: labelAxisLabel,
              ticks: {
                size: 0,
                format: (label: string) => truncateLabel(label, maxChars),
              },
              tickLabels: rotate ? { rotate: -35, anchor: "end" } : undefined,
            }
          : false,
      },
      y: valueAxis,
    },
  });
};
